import { Delete } from '@mui/icons-material';
import { ActionStatusLabels } from 'further-ui/labels';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { useLayoutContext } from 'contexts/LayoutContext';
import { LP_REALISATION_CREATE } from 'adminConstants/routes';
import Page from 'components/new/Page';
import Table, { type Column } from 'further-ui/components/Table';
import SearchField from 'further-ui/components/SearchField';
import AlertDialog from 'components/AlertDialog';
import ErrorBoundary from 'components/ErrorBoundary';
import PageFilters, { Filter } from 'components/new/PageFilters';
import DateRangePicker from 'further-ui/components/DateRangePicker';
import useListRealisations from './useListRealisations';
import { Visibility } from '@mui/icons-material';
import { createLpRealisationViewRoute } from 'adminConstants/routes';

const pageId = 'realisations';

const ListRealisations: React.FC = () => {
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const {
    realisations,
    totalCount,
    isFetching,
    filters,
    handleChangeFilters,
    handleDeleteRealisation,
    handleCloseDeleteDialog,
    handleOpenDeleteDialog,
    pagination,
    isDeleteDialogOpen,
  } = useListRealisations(pageId, selectedVehicleId ?? '');

  const columns: Array<Column<(typeof realisations)[number]>> = [
    {
      label: 'Asset Name',
      key: 'assetName',
    },
    {
      label: 'Status',
      key: 'status',
      render: (row) => ActionStatusLabels[row.status],
    },
    {
      label: 'Realisation Date',
      key: 'realisationDate',
      render: (row) => row.realisationDate.toLabel(),
    },
    {
      label: 'Units realised',
      key: 'numberOfUnitsRealised',
      render: (row) => numberToDisplayString(row.numberOfUnitsRealised),
    },
    {
      label: 'Total Amount (TCY)',
      key: 'totalRealisationAmountTcy',
      render: (row) =>
        numberToCurrencyString(row.totalRealisationAmountTcy, {
          currency: row.currencyCode,
        }),
    },
    {
      label: 'Total Amount (LCY)',
      key: 'totalRealisationAmountLcy',
      render: (row) =>
        numberToCurrencyString(row.totalRealisationAmountLcy, {
          currency: vehicle?.baseCurrency,
        }),
    },
  ];

  return (
    <Page
      heading="Realisations"
      actions={[{ label: 'Add realisation', link: LP_REALISATION_CREATE }]}
      spacing="lg"
    >
      <PageFilters justify="space-between">
        <Filter size="md">
          <SearchField
            placeholder="Search by asset name"
            value={filters.searchQuery}
            onSearch={(query) => {
              handleChangeFilters({ searchQuery: query });
            }}
            autoSearchOnDebounce
            allowEmptyQuery
            fullWidth
          />
        </Filter>
        <Filter size="sm">
          <Filter>
            <DateRangePicker
              dateRange={filters.dateRange}
              onDateRangeChange={(dateRange) => {
                handleChangeFilters({ dateRange });
              }}
              placeholder="Select date range"
            />
          </Filter>
        </Filter>
      </PageFilters>
      <ErrorBoundary>
        <Table
          columns={columns}
          rows={realisations}
          pagination={pagination}
          rowsTotalCount={totalCount}
          isLoading={isFetching}
          emptyMessage="No realisations found"
          actions={[
            {
              label: 'Delete realisation',
              icon: Delete,
              onClick: (row) => {
                handleOpenDeleteDialog(row.id);
              },
              disabled: (row) => !row.canModify,
              tooltip: (row) =>
                !row.canModify ? row.canModifyReason : undefined,
            },
            {
              label: 'View',
              icon: Visibility,
              link: (row) => createLpRealisationViewRoute(row.id),
            },
          ]}
        />
        {/* TODO: Style delete dialog when designs ready */}
        <AlertDialog
          onClose={handleCloseDeleteDialog}
          open={isDeleteDialogOpen}
          title="Are you sure you wish to delete this realisation?"
          content="This cannot be undone and all information relating to the realisation will be lost. If you have draft equalisations they will also be deleted."
          btnLabels={{ confirm: 'Delete realisation', cancel: 'Go back' }}
          cancelBtnProps={{ variant: 'contained', color: 'primary' }}
          confirmBtnProps={{
            variant: 'outlined',
            color: 'error',
            disabled: false,
          }}
          onConfirm={handleDeleteRealisation}
        />
      </ErrorBoundary>
    </Page>
  );
};

export default ListRealisations;
